import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/vue'
import type { RefreshResponse } from '@/types/auth'
import router from '@/router'

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'

/** Extract a short, safe string from an API error response body. */
function truncateDetail(data: unknown): string {
  if (!data) return ''
  if (typeof data === 'string') return data.slice(0, 512)
  const obj = data as Record<string, unknown>
  const msg = obj.detail ?? obj.message ?? obj.error
  if (typeof msg === 'string') return msg.slice(0, 512)
  return JSON.stringify(data).slice(0, 512)
}

/**
 * A message the user can read, from anything a request can throw: the API's
 * `detail` when there is one, otherwise `fallback`. Network failures and
 * non-HTTP errors deliberately get the fallback rather than axios's own text.
 */
export function extractApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    // The limiter's own wording is for developers ("Rate limit exceeded: 5 per 1 minute"); a used-up allowance
    // (`daily_analysis_limit`) says its own.
    const code = apiErrorCode(err)
    if (err.response?.status === 429 && (!code || code === 'rate_limited')) {
      return 'Too many requests — wait a minute and try again.'
    }
    return truncateDetail(err.response?.data) || fallback
  }
  return fallback
}

/** The API's machine-readable `code` (`email_not_verified`, `conflict`, …), or undefined. */
export function apiErrorCode(err: unknown): string | undefined {
  if (!axios.isAxiosError(err)) return undefined
  const code = (err.response?.data as { code?: unknown } | undefined)?.code
  return typeof code === 'string' ? code : undefined
}

/** HTTP status of a failed request, or undefined when it never got a response. */
export function apiErrorStatus(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined
}

/** True when the request gave up waiting (`ETIMEDOUT`, see `clarifyTimeoutError` below), not a refusal or a cancel. */
export function isTimeout(err: unknown): boolean {
  return axios.isAxiosError(err) && err.code === 'ETIMEDOUT'
}

// nginx answers every API request within 60 s (a 504 at worst) and the API's own budgets sit below that
// (ADR 0015, LISTING_IMPORT_DEADLINE_S). The client waits a little longer, so it never abandons a request the
// server is still finishing, and a dead connection still ends.
export const API_TIMEOUT_MS = 65_000

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  transitional: { clarifyTimeoutError: true },
  headers: {
    'Content-Type': 'application/json'
  }
})

// Bumped by every request that changes something (anything but GET). Lets a
// background refresh tell that the user edited while it was in flight, so it
// does not paint stale server state over the edit (workspace store loadTour).
let writeSeq = 0
export const apiWriteSeq = (): number => writeSeq

// Request interceptor to add access token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if ((config.method ?? 'get').toLowerCase() !== 'get') writeSeq++
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Queued token refresh: if multiple 401s fire concurrently, only one refresh runs
let refreshPromise: Promise<string> | null = null

function doRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'))
  }
  return axios
    .post<RefreshResponse>(`${api.defaults.baseURL}/auth/refresh`, {
      refresh_token: refreshToken,
    })
    .then((response) => {
      const { access_token } = response.data
      localStorage.setItem('accessToken', access_token)
      return access_token
    })
}

/** A fresh access token — one refresh at a time, shared with the interceptor
 *  below. For requests that don't go through axios (the SSE stream). */
export function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Skip token refresh for auth endpoints (401 is expected for login failures)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                           originalRequest.url?.includes('/auth/register')

    // If error is 401 and we haven't retried yet and it's not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      try {
        // Reuse in-flight refresh if one is already running
        const access_token = await refreshAccessToken()

        // Retry original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        const current = router.currentRoute.value
        router.push(
          current.name === 'login'
            ? { name: 'login' }
            : { name: 'login', query: { redirect: current.fullPath } },
        )
        return Promise.reject(refreshError)
      }
    }

    if (isTimeout(error)) {
      // Past nginx's own limit: the connection died, which the server never sees. Captured like a 5xx.
      Sentry.withScope((scope) => {
        scope.setTag('api.timeout', true)
        scope.setTag('api.method', originalRequest?.method?.toUpperCase() ?? 'UNKNOWN')
        scope.setContext('api_request', { url: originalRequest?.url })
        Sentry.captureException(error)
      })
    }

    const status = error.response?.status
    if (status) {
      const detail = truncateDetail(error.response!.data)

      if (status >= 500) {
        // Server errors — always capture as events
        Sentry.withScope((scope) => {
          scope.setTag('api.status', status)
          scope.setTag('api.method', originalRequest.method?.toUpperCase() ?? 'UNKNOWN')
          scope.setContext('api_request', {
            url: originalRequest.url,
            detail,
          })
          Sentry.captureException(error)
        })
      } else if (status !== 401) {
        // 4xx (except 401) — breadcrumb only, no event
        Sentry.addBreadcrumb({
          category: 'api',
          message: `${originalRequest.method?.toUpperCase()} ${originalRequest.url} → ${status}`,
          data: { detail },
          level: 'warning',
        })
      }
    }

    return Promise.reject(error)
  }
)

export default api
